import React from 'react';
import { useFamilyActivities, useSettings, useFestivalDays } from '../lib/usePublicData';

const ACCENTS = ['#fbe8a6', '#b8e6cf', '#f7c8d0', '#ecdef0']; // geel, mint, roze, lila

// Per festivaldag: korte label voor badge + pastel-achtergrond
const DAY_VISUALS = {
    'kleine-markt-11-07': { label: '11 JULI', bg: 'var(--yellow)' },
    'stadstuin-25-07':    { label: '25 JULI', bg: 'var(--pink)' },
    'grote-markt-29-08':  { label: '29 AUG',  bg: 'var(--mint)' },
};
const DayBadge = ({ label, bg, isAll = false }) => (
    <span
        data-testid={isAll ? 'family-badge-all' : `family-badge-${label}`}
        className="inline-block font-display tracking-wider"
        style={{
            backgroundColor: isAll ? 'var(--green)' : bg,
            color: isAll ? 'var(--cream)' : 'var(--green-dark)',
            border: isAll ? 'none' : '1.5px solid var(--green)',
            borderRadius: '999px',
            padding: '4px 10px',
            fontSize: '10px',
            letterSpacing: '0.06em',
            boxShadow: '0 2px 0 rgba(0, 107, 68, 0.25)',
            lineHeight: 1.1,
        }}
    >
        {label}
    </span>
);

const FamilyActivities = () => {
    const { data: items } = useFamilyActivities();
    const { data: settings } = useSettings();
    const { data: days } = useFestivalDays();
    if (!items || items.length === 0) return null;

    const totalDays = (days && days.length) || 3;
    const intro = settings?.family_intro_text
        || 'Voor de kleinste zotten is er een uitgebreid familie-aanbod met springplezier, animatie en kindergrime.';

    return (
        <section
            id="familie"
            data-testid="family-section"
            className="relative py-16 sm:py-24 px-5 sm:px-8"
        >
            <div className="mx-auto max-w-7xl">
                <header className="text-center mb-10 sm:mb-14">
                    <h2
                        data-testid="family-title"
                        className="zz-outline-title font-display"
                        style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}
                    >
                        Voor de kleinste zotten
                    </h2>
                    <p
                        data-testid="family-intro"
                        className="mt-4 text-base sm:text-lg text-[var(--green-dark)] max-w-2xl mx-auto leading-relaxed"
                    >
                        {intro}
                    </p>
                </header>

                <ul
                    data-testid="family-grid"
                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6"
                >
                    {items.map((it, idx) => {
                        const accent = ACCENTS[idx % ACCENTS.length];
                        const photo = it.image_url || '';
                        const ids = Array.isArray(it.festival_day_ids) ? it.festival_day_ids : [];
                        const allSelected = ids.length >= totalDays && totalDays > 0;
                        return (
                            <li
                                key={it.id}
                                data-testid={`family-card-${it.id}`}
                                className="rounded-2xl p-4 sm:p-5 flex flex-col items-center text-center border-2 border-[var(--green)]/40 shadow-[0_3px_0_var(--green-dark)] transition-transform hover:-translate-y-1"
                                style={{ backgroundColor: accent }}
                            >
                                <div
                                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-[3px] border-[var(--green-dark)] bg-cream flex items-center justify-center overflow-hidden mb-3"
                                    aria-hidden={photo ? 'false' : 'true'}
                                >
                                    {photo ? (
                                        <img
                                            src={photo}
                                            alt={it.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <span className="font-display text-2xl sm:text-3xl text-[var(--green-dark)]">
                                            {(it.name || '?').charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <h3 className="font-display text-sm sm:text-base text-[var(--green-dark)] leading-tight">
                                    {it.name}
                                </h3>
                                {ids.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5 justify-center" data-testid={`family-badges-${it.id}`}>
                                        {allSelected
                                            ? <DayBadge label="ALLE DAGEN" isAll />
                                            : ids.map((id) => {
                                                const v = DAY_VISUALS[id];
                                                if (!v) return null;
                                                return <DayBadge key={id} label={v.label} bg={v.bg} />;
                                            })}
                                    </div>
                                )}
                                {it.description && (
                                    <p className="mt-1.5 text-xs sm:text-sm text-[var(--green-dark)]/85 leading-snug">
                                        {it.description}
                                    </p>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
};

export default FamilyActivities;
